import cn from 'classnames';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../UI/Button/Button';
import { Checkbox } from '../../UI/Checkbox/Checkbox';
import { HidePasswordIcon, VisiblePasswordIcon } from '../../assets/icons';
import { scrollAvailable } from '../../helpers/scrollToggler';
import { useAppDispatch } from '../../hooks/reduxHooks/useAppDispatch';
import { PATH_DASHBOARD } from '../../routes/RouterConfig';
import { registerOrganization } from '../../services/Register';
import { getOrganizationRegisterSchema } from '../../validations/organizationValidation';
import styles from './Register.module.scss';


interface OrganizationFormValues {
    full_title: string;
    short_title: string;
    email: string;
    password: string;
    confirm_password: string;
}

const OrganizationRegisterForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

    const initialValues: OrganizationFormValues = {
        full_title: '',
        short_title: '',
        email: '',
        password: '',
        confirm_password: '',
    };

    const formik = useFormik<OrganizationFormValues>({
        initialValues,
        validationSchema: getOrganizationRegisterSchema(),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await dispatch(registerOrganization(values));
                navigate(PATH_DASHBOARD.root);
                scrollAvailable();
            } catch (error) {
                toast.error('Произошла ошибка сервера');
            } finally {
                setSubmitting(false);
            }
        },
        validateOnMount: true,
    });

    return (
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <div className={styles.inputs__wrapper}>
                <label className={styles.input__label}>
                    <input
                        className={styles.input}
                        placeholder={'Полное наименование организации'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.full_title}
                        name={'full_title'}
                        style={{
                            borderColor: formik.touched.full_title && formik.errors.full_title
                                ? 'red'
                                : formik.touched.full_title && !formik.errors.full_title
                                    ? 'green'
                                    : 'grey',
                            background: 'inherit',
                        }}
                    />
                    {formik.touched.full_title && formik.errors.full_title && (
                        <p className={styles.input__error}>{formik.errors.full_title}</p>
                    )}
                </label>

                <label className={styles.input__label}>
                    <input
                        className={styles.input}
                        placeholder={'Краткое наименование организации'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.short_title}
                        name={'short_title'}
                        style={{
                            borderColor:
                                formik.touched.short_title && formik.errors.short_title
                                    ? 'red'
                                    : formik.touched.short_title && !formik.errors.short_title
                                        ? 'green'
                                        : 'grey',
                            background: 'inherit',
                        }}
                    />
                    {formik.touched.short_title && formik.errors.short_title && (
                        <p className={styles.input__error}>{formik.errors.short_title}</p>
                    )}
                </label>

                <label className={styles.input__label}>
                    <input
                        className={styles.input}
                        placeholder={'Email'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        name={'email'}
                        style={{
                            borderColor:
                                formik.touched.email && formik.errors.email
                                    ? 'red'
                                    : formik.touched.email && !formik.errors.email
                                        ? 'green'
                                        : 'grey',
                            background: 'inherit',
                        }}
                    />
                    {formik.touched.email && formik.errors.email && (
                        <p className={styles.input__error}>{formik.errors.email}</p>
                    )}
                </label>

                <label className={styles.input__label}>
                    <div className={styles.input__password}>
                        <input
                            type={isPasswordVisible ? 'text' : 'password'}
                            className={styles.input}
                            placeholder={'Пароль'}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            name={'password'}
                            style={{
                                borderColor:
                                    formik.touched.password && formik.errors.password
                                        ? 'red'
                                        : formik.touched.password && !formik.errors.password
                                            ? 'green'
                                            : 'grey',
                                background: 'inherit',
                            }}
                        />
                        <div
                            className={styles.password__icon}
                            onClick={() => setIsPasswordVisible((s) => !s)}
                        >
                            {isPasswordVisible ? <VisiblePasswordIcon /> : <HidePasswordIcon />}
                        </div>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                        <p className={styles.input__error}>{formik.errors.password}</p>
                    )}
                </label>

                <label className={styles.input__label}>
                    <div className={styles.input__password}>
                        <input
                            type={isConfirmPasswordVisible ? 'text' : 'password'}
                            className={styles.input}
                            placeholder={'Повторите пароль'}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.confirm_password}
                            name={'confirm_password'}
                            style={{
                                borderColor:
                                    formik.touched.confirm_password && formik.errors.confirm_password
                                        ? 'red'
                                        : formik.touched.confirm_password && !formik.errors.confirm_password
                                            ? 'green'
                                            : 'grey',
                                background: 'inherit',
                            }}
                        />
                        <div
                            className={styles.password__icon}
                            onClick={() => setIsConfirmPasswordVisible((s) => !s)}
                        >
                            {isConfirmPasswordVisible ? <VisiblePasswordIcon /> : <HidePasswordIcon />}
                        </div>
                    </div>
                    {formik.touched.confirm_password && formik.errors.confirm_password && (
                        <p className={styles.input__error}>{formik.errors.confirm_password}</p>
                    )}
                </label>
            </div>

            <Checkbox text={'Запомнить пароль'} className={styles.inputs__checkbox} />

            <div className={styles.inputs__buttons}>
                <Button
                    isDisabled={!formik.isValid || !formik.dirty} // кнопка доступна только если валидно и есть изменения
                    buttonType={'submit'}
                    className={cn(styles.inputs__button, {
                        [styles.inputs__button_valid]: formik.dirty && formik.isValid,
                    })}
                >
                    Создать профиль
                </Button>
            </div>
        </form>
    );
};

export default OrganizationRegisterForm;
